/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment } from 'react';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-react-renderer';

import { filterContentfulArray } from '../utils/regionLockedContent';
import { useProvince } from '../state/ProvinceProvider';

import Carousel from './Carousel';
import SectionGraphic from './SectionGraphic';
import SectionContainer from './SectionContainer';
import RichText from './RichTextV2';
import { Body3Css } from './Typography';

const IntroBox = styled.div`
  margin-bottom: 30px;
  text-align: center;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    margin-bottom: 40px;
  }
`;

const Heading = styled.h2`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.heading2.fontWeight')};
  font-size: ${themeGet('typography.mobile.heading2.fontSize')};
  line-height: ${themeGet('typography.mobile.heading2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.heading2.letterSpacing')};
  margin: 0 0 10px 0;
  text-align: center;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading2.fontSize')};
    line-height: ${themeGet('typography.desktop.heading2.lineHeight')};
    margin: 0 0 40px 0;

    &.lowerCost {
      text-align: left;
    }
  }
`;

const IntroText = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('typography.mobile.body2.fontWeight')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};
  margin: 0 0 10px 0;

  &:last-child {
    margin: 0;
    font-size: ${themeGet('typography.mobile.body3.fontSize')};
    line-height: ${themeGet('typography.mobile.body3.lineHeight')};
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading4.fontSize')};
    line-height: ${themeGet('typography.desktop.heading4.lineHeight')};

    &:last-child {
      font-size: ${themeGet('typography.desktop.body3.fontSize')};
      line-height: ${themeGet('typography.desktop.body3.lineHeight')};
    }
  }
`;

const Card = styled.div`
  border-radius: 12px;
  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.1);
  background-color: ${themeGet('colors.white')};
  padding: 20px;
  height: 100%;
  min-height: 188px;
  margin: 0 7.5px;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    min-height: 242px;
    padding: 30px;
    margin: 0 15px;
  }
`;

const CardTitle = styled.h3`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: ${themeGet('fontWeights.medium')};
  font-size: ${themeGet('typography.mobile.body2.fontSize')};
  line-height: ${themeGet('typography.mobile.body2.lineHeight')};
  letter-spacing: ${themeGet('typography.mobile.body2.letterSpacing')};
  color: ${({ theme }) => theme.colors['primary-text']};
  margin: 0 0 10px 0;
  text-align: left;

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.heading4.fontSize')};
    line-height: ${themeGet('typography.desktop.heading4.lineHeight')};
  }
`;

const CardText = styled.p`
  font-family: ${themeGet('typography.mobile.heading1.fontFamily')};
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors['primary-text']};

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    font-size: ${themeGet('typography.desktop.body3.fontSize')};
    line-height: ${themeGet('typography.desktop.body3.lineHeight')};
  }
    text-align: left;
`;

const CardLink = styled.a`
  ${Body3Css};
  text-decoration: underline;
`;

const CardCarouselOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading>{children}</Heading>,
    [BLOCKS.PARAGRAPH]: (_, children) => <IntroText>{children}</IntroText>,
  },
};

const CardOptions: Options = {
  renderNode: {
    [BLOCKS.HEADING_3]: (_, children) => <CardTitle>{children}</CardTitle>,
    [BLOCKS.PARAGRAPH]: (_, children) => <CardText>{children}</CardText>,
    [INLINES.HYPERLINK]: (node, children) => <CardLink href={node.data.uri}>{children}</CardLink>,
  },
};

interface CardCarouselProps {
  data: any;
  isRegionLocked?: boolean;
}

const CardCarousel = ({
  data,
  isRegionLocked = false,
}: CardCarouselProps) => {
  const { activeProvince } = useProvince();

  if (!data) return null;

  const {
    sectionId,
    imageDamAsset,
    combinatorSectionItems,
    sectionTitle,
    sectionSubtitle,
    backgroundColor,
  } = data;
  const cardItems = isRegionLocked
    ? filterContentfulArray(activeProvince, combinatorSectionItems) : combinatorSectionItems;
  if (cardItems.length === 0) return null;

  return (
    <SectionGraphic
      id={sectionId}
      background={backgroundColor}
      img={imageDamAsset}
    >
      <SectionContainer>
        <IntroBox>
          <RichText
            document={sectionTitle}
            options={CardCarouselOptions}
          />
          <RichText document={sectionSubtitle} options={CardCarouselOptions} />
        </IntroBox>
        <Carousel
          id="card-carousel"
          data-testid="Carousel"
          items={(cardItems as any[]).map(({ id, headingText, paragraphs }) => (
            <Card key={id}>
              <CardTitle>
                {headingText}
              </CardTitle>
              {(paragraphs as any[]).map(({ id: key, paragraphRTF }) => (
                <Fragment key={key}>
                  <RichText document={paragraphRTF} options={CardOptions} />
                </Fragment>
              ))}
            </Card>
          ))}
        />
      </SectionContainer>
    </SectionGraphic>
  );
};

export default CardCarousel;
